import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Check, X, BadgeCheck, UserMinus, Ban, MessageCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

const BuddyRow = ({ u, actions }) => (
  <div className="t-card p-4 flex items-center gap-3" data-testid={`buddy-row-${u.id}`}>
    <div className="w-10 h-10 rounded-full bg-secondary/30 flex items-center justify-center font-bold">{u.name[0]}</div>
    <div className="flex-1 min-w-0">
      <p className="font-bold text-sm flex items-center gap-1 truncate">
        {u.name}
        {u.verification_status === "verified" && <BadgeCheck className="w-4 h-4 text-primary shrink-0" />}
      </p>
      <p className="text-xs text-muted-foreground truncate">{u.bio || `${u.completed_rides || 0} rides completed`}</p>
    </div>
    {actions}
  </div>
);

export default function Buddies() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(params.get("tab") || "buddies");
  const [buddies, setBuddies] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [discover, setDiscover] = useState([]);

  const load = useCallback(() => {
    api.get("/buddies").then(({ data }) => setBuddies(data)).catch(() => {});
    api.get("/buddies/requests").then(({ data }) => setRequests(data)).catch(() => {});
    api.get("/buddies/discover").then(({ data }) => setDiscover(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendRequest = async (userId) => {
    try { await api.post("/buddies/request", { user_id: userId }); toast.success("Buddy request sent!"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const respond = async (reqId, action) => {
    try { await api.post(`/buddies/requests/${reqId}/respond`, { action }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (userId) => {
    try { await api.delete(`/buddies/${userId}`); toast.success("Buddy removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const block = async (userId) => {
    try { await api.post(`/buddies/${userId}/block`); toast.success("User blocked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openChat = async (userId) => {
    try {
      const { data } = await api.post("/chat/open", { type: "buddy", ref_id: userId });
      navigate(`/chat?open=${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="buddies-page">
      <h1 className="font-display font-black text-3xl">Buddies</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full">
          <TabsTrigger data-testid="tab-my-buddies" value="buddies" className="rounded-full">My Buddies ({buddies.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-buddy-requests" value="requests" className="rounded-full">
            Requests {requests.incoming.length > 0 && <span className="ml-1 bg-primary text-white rounded-full px-1.5 text-xs">{requests.incoming.length}</span>}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-discover" value="discover" className="rounded-full">Discover</TabsTrigger>
        </TabsList>

        <TabsContent value="buddies" className="mt-6 space-y-3">
          {buddies.length ? buddies.map((u) => (
            <BuddyRow key={u.id} u={u} actions={
              <div className="flex gap-2">
                <button data-testid={`chat-buddy-${u.id}`} onClick={() => openChat(u.id)} title="Chat"
                  className="w-9 h-9 rounded-full bg-secondary/20 text-[#14532D] flex items-center justify-center hover:bg-secondary/40 transition-colors duration-200"><MessageCircle className="w-4 h-4" /></button>
                <button data-testid={`remove-buddy-${u.id}`} onClick={() => remove(u.id)} title="Remove buddy"
                  className="w-9 h-9 rounded-full bg-black/5 flex items-center justify-center hover:bg-black/10 transition-colors duration-200"><UserMinus className="w-4 h-4" /></button>
                <button data-testid={`block-buddy-${u.id}`} onClick={() => block(u.id)} title="Block"
                  className="w-9 h-9 rounded-full bg-destructive/10 text-destructive flex items-center justify-center hover:bg-destructive/20 transition-colors duration-200"><Ban className="w-4 h-4" /></button>
              </div>
            } />
          )) : (
            <div className="t-card p-8 text-center text-sm text-muted-foreground" data-testid="no-buddies">
              No buddies yet. Head to <button onClick={() => setTab("discover")} className="font-bold text-primary">Discover</button> to find your people.
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests" className="mt-6 space-y-6">
          <section>
            <h2 className="font-display font-bold text-sm mb-3 uppercase tracking-[0.2em] text-muted-foreground">Incoming</h2>
            {requests.incoming.length ? requests.incoming.map((r) => (
              <div key={r.id} className="mb-3">
                <BuddyRow u={{ ...r.user, verification_status: "verified" }} actions={
                  <div className="flex gap-2">
                    <button data-testid={`accept-buddy-${r.id}`} onClick={() => respond(r.id, "accept")}
                      className="w-9 h-9 rounded-full bg-secondary/20 text-[#14532D] flex items-center justify-center hover:bg-secondary/40 transition-colors duration-200"><Check className="w-4 h-4" /></button>
                    <button data-testid={`reject-buddy-${r.id}`} onClick={() => respond(r.id, "reject")}
                      className="w-9 h-9 rounded-full bg-destructive/10 text-destructive flex items-center justify-center hover:bg-destructive/20 transition-colors duration-200"><X className="w-4 h-4" /></button>
                  </div>
                } />
              </div>
            )) : <p className="text-sm text-muted-foreground" data-testid="no-incoming-requests">No incoming requests.</p>}
          </section>
          <section>
            <h2 className="font-display font-bold text-sm mb-3 uppercase tracking-[0.2em] text-muted-foreground">Sent</h2>
            {requests.outgoing.length ? requests.outgoing.map((r) => (
              <div key={r.id} className="mb-3">
                <BuddyRow u={{ ...r.user, verification_status: "verified" }} actions={<span className="t-chip bg-[#F9C31F] text-[#12211C]">pending</span>} />
              </div>
            )) : <p className="text-sm text-muted-foreground">No pending sent requests.</p>}
          </section>
        </TabsContent>

        <TabsContent value="discover" className="mt-6 space-y-3">
          {discover.length ? discover.map((u) => (
            <BuddyRow key={u.id} u={{ ...u, verification_status: "verified" }} actions={
              u.request_pending ? (
                <span className="t-chip bg-[#F9C31F] text-[#12211C]">pending</span>
              ) : (
                <Button data-testid={`add-buddy-${u.id}`} size="sm" onClick={() => sendRequest(u.id)} className="rounded-full font-bold">
                  <UserPlus className="w-4 h-4 mr-1" /> Add
                </Button>
              )
            } />
          )) : (
            <div className="t-card p-8 text-center text-sm text-muted-foreground" data-testid="no-discover">
              No other verified members yet — you're early! Invite your campus friends to join THIRI.
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
